import * as d3 from "d3";
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  SimpleChange,
  ViewChild,
} from "@angular/core";
import { ProjectService } from "../../shared/services/project.service";
import { HttpService } from "../../shared/services/http.service";
import { ImageLoaderService } from "../../shared/services/image-loader.service";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { ImageFlipperService } from "../image-flipper.service";
import { forkJoin } from "rxjs";
import { CachedProjectService } from "../../shared/services/cached-project.service";
import { map } from "rxjs/operators";
import { ShotOverlayDto } from "../../models/shotOverlayDto";
import { AppConfig } from "../../app.config";

interface ImageInformation {
  job_name: string;
  iteration: number;
  job_id: string;
  total_iterations: number;
  x2_is_inline: boolean;
  slice_number?: string;
  dimension?: string;
  crop_shell?: boolean;
  model_grid?: {
    dx: number;
    nx1: number;
    nx2: number;
    nx3: number;
    is_2d: boolean;
  };
}

interface PlotResult {
  png_plot_url: string;
  png_colorbar_url: string;
  model_slice_url: string;
  min_value: number;
  max_value: number;
  palette: string;
  true_min_value: number;
  true_max_value: number;
  model_slice_api_version: number;
}

interface PlotParams {
  palette: string | null;
  type: string | null;
  min_val: number | null;
  max_val: number | null;
  slice_dim?: number;
  slice_number?: number;
  vp_ref?: number | null;
}

/**
 * User-driven crop limits, all in real-world meters. These are applied
 * client-side (see resizeAndCropBase64Image) and are deliberately kept out of
 * the plot request params — the plot endpoint knows nothing about them. They
 * are persisted into the saved image_link separately (build_persistable_link).
 */
interface CropParams {
  enabled: boolean;
  x_min: number | null;
  x_max: number | null;
  y_min: number | null;
  y_max: number | null;
}

interface StructuredResponse {
  image_information: ImageInformation;
  image_link: string;
}

@Component({
    selector: "app-slice-image-visualizer",
    templateUrl: "./slice-image-visualizer.component.html",
    styleUrls: ["./slice-image-visualizer.component.less"],
    standalone: false
})

/**
 * SliceImageVisualizerComponent
 *
 * This Angular component is responsible for visualizing 2D slice images, typically used in scientific or geophysical applications.
 * It handles fetching, processing, cropping, and displaying images on an SVG canvas, and supports dynamic resizing and image flipping.
 *
 * ## Features
 * - Fetches image data from a remote server using provided parameters.
 * - Parses image URLs and extracts query parameters for flexible image requests.
 * - Renders images onto an SVG element using D3.js.
 * - Supports cropping and resizing of images based on real-world coordinates.
 * - Handles errors gracefully and notifies users of issues.
 * - Integrates with an image flipper service for browsing multiple images.
 * - Dynamically resizes the visualization on window resize events.
 *
 * @example
 * ```html
 * <app-slice-image-visualizer [messageContent]="response"></app-slice-image-visualizer>
 * ```
 *
 * ## Properties
 *
 * @property {ElementRef} sliceImageContainer - Reference to the container element for the SVG image.
 * @property {StructuredResponse | null} messageContent - Input data containing image information and links.
 * @property {boolean} drawer_visible - Controls the visibility of the settings drawer.
 * @property {string | null} plot_link - The base URL for the image plot.
 * @property {string | null} image_plot_url - The URL of the fetched image plot.
 * @property {string} image_object_url - The Base64 or Blob URL of the image to display.
 * @property {ImageInformation} image_information - Metadata about the image (e.g., job name, iteration, grid info).
 * @property {PlotParams} image_plot_parameters - Parameters for image plotting (palette, type, min/max values, etc.).
 * @property {number | null} true_min_value - The true minimum value in the image data.
 * @property {number | null} true_max_value - The true maximum value in the image data.
 * @property {string | null} image_colorbar_url - The URL for the image colorbar.
 * @property {number} width - The width of the SVG canvas.
 * @property {number} height - The height of the SVG canvas.
 * @property {any} svg - The D3 SVG element used for rendering.
 * @property {boolean} hide_settings - Controls the visibility of settings UI.
 *
 * ## Methods
 *
 * @method ngOnInit - Angular lifecycle hook for initialization.
 * @method ngAfterViewInit - Initializes the SVG canvas after the view is loaded.
 * @method ngOnChanges - Handles changes to input properties and triggers image fetching.
 * @method resolve_image_request_default_params - Parses the image link and extracts query parameters for plotting.
 * @method convert_to_URL_params - Converts a dictionary of parameters into a URL query string.
 * @method get_image - Fetches the image from the server and prepares it for display.
 * @method plot_image - Renders the image onto the SVG canvas, handling cropping and errors.
 * @method update_image_width - Updates the SVG and image width on container resize.
 * @method add_image_to_flipper - Adds the current image to the image flipper service.
 * @method resizeAndCropBase64Image - Crops and resizes a Base64 image based on real-world coordinates.
 * @method sizeChange - Host listener for window resize events to trigger responsive updates.
 *
 * ## Dependencies
 * - HttpService: For HTTP requests.
 * - ProjectService: For authorization and project context.
 * - ImageLoaderService: For loading and converting images.
 * - NzNotificationService: For user notifications.
 * - ImageFlipperService: For managing image flipping/browsing.
 *
 * ## Usage
 * Place this component in your template and provide a valid `messageContent` input with image information and links.
 */
export class SliceImageVisualizerComponent {
  @ViewChild("sliceImageContainer") sliceImageContainer: ElementRef;
  @ViewChild("chartTooltip", { static: false }) tooltipDivRef!: ElementRef;

  @Input() messageContent: null | StructuredResponse = null;
  // Emitted when the user applies parameter changes (the "Update" button) with
  // the rebuilt image_link (params encoded as query string) so the parent can
  // persist it back into the saved conversation's image_links.
  @Output() contentChange = new EventEmitter<string>();
  drawer_visible: boolean = false;
  plot_link: string | null = null;
  image_plot_url: string | null;
  image_object_url: string;
  image_information: ImageInformation = {
    job_name: "",
    iteration: null,
    job_id: "",
    x2_is_inline: false,
    total_iterations: null,
  };
  image_plot_parameters: PlotParams = {
    palette: "SEGRefl2",
    type: "Vp",
    min_val: 1500,
    max_val: 5000,
  };
  true_min_value: number | null = null;
  true_max_value: number | null = null;
  image_colorbar_url: string | null = null;

  // Manual crop limits in meters. Defaults are seeded to the full model extent
  // (init_crop_defaults) once the model_grid is known, so the inputs start at
  // the full range and the user narrows them down.
  crop_params: CropParams = {
    enabled: false,
    x_min: null,
    x_max: null,
    y_min: null,
    y_max: null,
  };

  width: number = 500;
  height: number = 400;
  svg: any = null;
  hide_settings: boolean = true;

  centered_model_range: boolean = false;
  loading: boolean = false;
  show_shots: boolean = false;
  hide_shots_without_trace_fit: boolean = false;

  xScale: any;
  yScale: any;

  trace_color_min: number = 70;
  trace_color_max: number = 80;
  loading_shot: boolean = false;
  shot_radius: number = 3;
  shot_tooltip: any;

  constructor(
    private httpService: HttpService,
    private projectService: ProjectService,
    private imageService: ImageLoaderService,
    private notificationService: NzNotificationService,
    private imageFlipper: ImageFlipperService,
    private cachedProjectService: CachedProjectService
  ) {}

  ngOnInit() {}

  ngAfterViewInit() {
    const containerElement = this.sliceImageContainer?.nativeElement;
    this.width = containerElement?.clientWidth;

    this.svg = d3
      .create("svg")
      .attr("width", this.width)
      .attr("height", this.height);

    // Append the SVG element to DOM.
    let chartElement = this.sliceImageContainer?.nativeElement as HTMLElement;
    if (chartElement) chartElement.append(this.svg.node());

    this.shot_tooltip = d3.select(this.tooltipDivRef.nativeElement);
  }

  async ngOnChanges(changes: SimpleChange) {
    try {
      this.resolve_image_request_default_params();
      this.init_crop_defaults();
      await this.get_image();
    } catch (e) {
      console.log("error here in processing", e);
    }
  }

  /**
   * Resolves and processes an image URL by splitting it into base URL and query parameters.
   * Extracts query parameters from the image link and stores them in image_plot_parameters.
   * Sets the base URL to plot_link property.
   * If the image link is a relative path, prepends the API URL from config.
   *
   * @throws {Error} If no image link is found in messageContent
   *
   * @example
   * // For image URL: "/jobs/123/plot/40/?slice_dim=1&slice_number=0"
   * // Will set:
   * // plot_link = "https://api.example.com/api/v1/jobs/123/plot/40/"
   * // image_plot_parameters = { slice_dim: "1", slice_number: "0" }
   */
  resolve_image_request_default_params() {
    this.image_information = this.messageContent.image_information;
    let imageLink = this.messageContent.image_link;
    
    if (!imageLink) {
      throw new Error("Could not find image link to use");
    }

    // If the image link is a relative path (starts with /), prepend the API URL
    if (imageLink.startsWith('/')) {
      imageLink = `${AppConfig.settings.apiUrl}${imageLink}`;
    }

    const [baseUrl, params] = imageLink.split("?");
    if (params) {
      const urlParams = new URLSearchParams(params);
      urlParams.forEach((value, key) => {
        // Crop bounds are persisted in the link but must not leak into the plot
        // request params, so route them into crop_params instead.
        if (key.startsWith("crop_")) {
          this.apply_persisted_crop_param(key, value);
        } else {
          this.image_plot_parameters[key] = value;
        }
      });
    }
    this.plot_link = baseUrl;
  }

  /**
   * Restores a single persisted crop bound (e.g. "crop_x_min=11000") into
   * crop_params, enabling the crop. Ignores keys/values that don't parse.
   */
  private apply_persisted_crop_param(key: string, value: string) {
    const num = parseFloat(value);
    if (isNaN(num)) return;
    switch (key) {
      case "crop_x_min":
        this.crop_params.x_min = num;
        break;
      case "crop_x_max":
        this.crop_params.x_max = num;
        break;
      case "crop_y_min":
        this.crop_params.y_min = num;
        break;
      case "crop_y_max":
        this.crop_params.y_max = num;
        break;
      default:
        return;
    }
    this.crop_params.enabled = true;
  }

  /**
   * Converts a dictionary into URL parameters.
   * @param params - Dictionary of key-value pairs
   * @param boto3 - This appends a boto3 key to the params which allows the call to be that of boto3 (the new image generation version)
   * @returns URL parameter string starting with '?'
   */
  private convert_to_URL_params(
    params: Record<string, any>,
    boto3: boolean = true
  ): string {
    const urlParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        urlParams.append(key, value.toString());
      }
    });
    urlParams.append("boto3", String(boto3));

    return `?${urlParams.toString()}`;
  }

  /**
   * Rebuilds the image_link for persistence: the base plot URL with the current
   * plot parameters re-encoded as the query string. The `boto3` transport flag is
   * left off (it is re-applied on fetch). The leading API URL is stripped so the
   * stored link stays relative — matching how it arrived and keeping it portable
   * across environments. On reload resolve_image_request_default_params reads
   * these params straight back into image_plot_parameters.
   */
  private build_persistable_link(): string {
    const urlParams = new URLSearchParams();
    Object.entries(this.image_plot_parameters).forEach(([key, value]) => {
      if (key === "boto3") return;
      if (value !== null && value !== undefined) {
        urlParams.append(key, value.toString());
      }
    });

    // Crop bounds live outside image_plot_parameters (they must not be sent to
    // the plot endpoint), so append them here when a crop is active.
    if (this.crop_params.enabled) {
      const crop_entries: [string, number | null][] = [
        ["crop_x_min", this.crop_params.x_min],
        ["crop_x_max", this.crop_params.x_max],
        ["crop_y_min", this.crop_params.y_min],
        ["crop_y_max", this.crop_params.y_max],
      ];
      crop_entries.forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          urlParams.append(key, value.toString());
        }
      });
    }

    let link = `${this.plot_link}?${urlParams.toString()}`;
    const apiUrl = AppConfig.settings.apiUrl;
    if (apiUrl && link.startsWith(apiUrl)) {
      link = link.slice(apiUrl.length);
    }
    return link;
  }

  /**
   * Asynchronously retrieves an image from a specified plot link.
   *
   * @throws {Error} If there's an error fetching the image from the server
   *
   * @remarks
   * This method requires a valid plot_link to be set before calling.
   * It uses the project service's authorization headers for the HTTP request.
   * If plot_link is not set, the method returns early without making a request.
   *
   * @param persist When true (the user clicked "Update"), the resolved plot
   * parameters are re-encoded into the image_link and emitted so the parent can
   * save them to the conversation. Auto-fetches from ngOnChanges pass false.
   */
  async get_image(persist: boolean = false) {
    if (!this.plot_link) return;
    let plot_link_with_params: string =
      this.plot_link + this.convert_to_URL_params(this.image_plot_parameters);
    this.loading = true;
    try {
      const response = await this.httpService
        .get(plot_link_with_params, {
          headers: this.projectService.getAuthorizationHeader(),
        })
        .toPromise()
        .then(async (response: PlotResult) => {
          // console.log("then", response);
          this.image_plot_url = response.png_plot_url;
          this.image_colorbar_url = response.png_colorbar_url;
          this.image_plot_parameters.max_val = response.max_value;
          this.image_plot_parameters.min_val = response.min_value;

          this.image_object_url = await this.imageService
            .getImageBase64(this.image_plot_url)
            .toPromise();

          // console.log(this.image_object_url);
          this.plot_image();

          if (persist) {
            this.contentChange.emit(this.build_persistable_link());
          }
        })
        .catch((error) => {
          console.log("error caught in promise", error);
          this.notificationService.error(
            "Error",
            error?.error?.detail || "An unexpected error occurred"
          );
        });
    } catch (error) {
      console.error("Error fetching image:", error);
      this.notificationService.error(
        "Error",
        error?.error?.detail || "An unexpected error occurred"
      );
    }
    this.loading = false;
  }

  /**
   * Plots an image on the SVG canvas.
   * First removes any existing image with the class 'slice-image'.
   * Then appends a new image using the stored Blob URL.
   *
   * The function handles both successful and failed image loading:
   * - On successful load, it revokes the Object URL to free memory
   * - On error, it displays an error message on the SVG canvas and shows a notification
   *
   * The image is scaled to fit the SVG dimensions while preserving aspect ratio.
   *
   * @throws {Error} Logs error to console and shows notification if image fails to load
   */
  async plot_image() {
    // Remove existing image if it exists
    const existingImage = this.svg.select(".slice-image");
    if (!existingImage.empty()) {
      existingImage.remove();
    }

    let startX = 0;
    let startY = 0;
    let endX = 0;
    let endY = 0;

    // Full model extent in meters (the un-cropped bounds for this dimension).
    const { fullX, fullY } = this.get_full_extent();
    endX = fullX;
    endY = fullY;

    // Render from the original image by default; only swap in a cropped canvas
    // when a crop is active. image_object_url is kept pristine so re-applying a
    // different crop always re-crops from the full-resolution source.
    let display_url = this.image_object_url;

    const crop = this.resolve_crop_bounds(fullX, fullY);
    if (crop) {
      startX = crop.startX;
      endX = crop.endX;
      startY = crop.startY;
      endY = crop.endY;
      display_url = await this.resizeAndCropBase64Image(
        this.image_object_url,
        startX,
        endX,
        fullX,
        startY,
        endY,
        fullY
      );
    }

    // give the start and end domains with the widths of the images. l
    this.xScale = d3
      .scaleLinear()
      .domain([startX, endX])
      .range([0, this.width]);

    if (this.image_information.model_grid.is_2d) {
      // the yScale is inverted in the way that our co-ordinate system is working when its a 2D job since top is 0 depth
      this.yScale = d3
        .scaleLinear()
        .domain([endY, startY - 200])
        .range([this.height, 0]);
    } else {
      this.yScale = d3
        .scaleLinear()
        .domain([startY, endY])
        .range([this.height, 0]);
    }

    // Add an image to the SVG
    this.svg
      .append("image")
      .attr("class", "slice-image")
      .attr("xlink:href", display_url)
      .on("load", () => {
        // Revoke the rendered URL once it has loaded to free memory. Only the
        // displayed URL is revoked — image_object_url (the original) is kept so
        // the crop can be re-applied without re-fetching.
        URL.revokeObjectURL(display_url);
        this.remove_shots_on_depth_chart();
        this.onChangeShowShots();
      })
      .on("error", () => {
        console.error("Error loading image from Blob URL.");
        this.notificationService.error(
          "Error",
          "Error loading image from Blob URL."
        );
        // Handle error, e.g., display a placeholder or message
        this.svg
          .append("text")
          .attr("x", this.width / 2)
          .attr("y", this.height / 2)
          .attr("text-anchor", "middle")
          .attr("fill", "red")
          .text("Failed to load image.");
        URL.revokeObjectURL(display_url); // Still revoke on error
      })
      .attr("preserveAspectRatio", "none")
      .attr("width", this.width) // Desired width of the image
      .attr("height", this.height); // Desired height of the image
  }

  async update_image_width() {
    const containerElement = this.sliceImageContainer.nativeElement;
    await new Promise((resolve) => setTimeout(resolve, 100));
    this.width = containerElement.clientWidth;
    this.svg.attr("width", this.width);
    this.svg.select("image.slice-image").attr("width", this.width);
  }

  add_image_to_flipper() {
    var svgString = new XMLSerializer().serializeToString(this.svg.node()); // Serialize the SVG element to a string
    var base64 = window.btoa(svgString); // Convert the string to a base64 encoded string
    var dataURI = "data:image/svg+xml;base64," + base64; // Create the data URI
    this.imageFlipper.add_image({
      image_link: dataURI,
      image_information: this.image_information,
    });
  }

  /**
   * Full model extent in meters for the current slice dimension. Mirrors the
   * grid maths used when plotting and is the basis for both the default crop
   * limits and clamping. Returns {0, 0} when the grid/dimension is unknown
   * (e.g. some 2D jobs), which callers treat as "crop unavailable".
   */
  private get_full_extent(): { fullX: number; fullY: number } {
    const grid = this.image_information.model_grid;
    if (!grid) return { fullX: 0, fullY: 0 };
    const dx = Number(grid.dx);
    switch (this.image_information.dimension) {
      case "inline":
        return { fullX: Number(grid.nx1) * dx, fullY: Number(grid.nx3) * dx };
      case "crossline":
        return { fullX: Number(grid.nx2) * dx, fullY: Number(grid.nx3) * dx };
      case "depth":
        return { fullX: Number(grid.nx1) * dx, fullY: Number(grid.nx2) * dx };
      default:
        return { fullX: 0, fullY: 0 };
    }
  }

  /**
   * Whether a crop can be applied for the current image — true only when the
   * model extent is known, so the crop UI is hidden for slices we can't map
   * meters onto.
   */
  get crop_available(): boolean {
    const { fullX, fullY } = this.get_full_extent();
    return fullX > 0 && fullY > 0;
  }

  /**
   * Seeds the crop inputs to the full model extent the first time the extent is
   * known, so the fields open at the full range. Values already restored from a
   * persisted link (non-null) are left untouched.
   */
  private init_crop_defaults() {
    const { fullX, fullY } = this.get_full_extent();
    if (fullX <= 0 || fullY <= 0) return;
    if (this.crop_params.x_min == null) this.crop_params.x_min = 0;
    if (this.crop_params.x_max == null) this.crop_params.x_max = fullX;
    if (this.crop_params.y_min == null) this.crop_params.y_min = 0;
    if (this.crop_params.y_max == null) this.crop_params.y_max = fullY;
  }

  /**
   * Resolves the crop window (meters) to apply when plotting, or null for no
   * crop. A user-enabled manual crop takes precedence; otherwise the legacy
   * Shell-model presets (crop_shell) are used. Manual bounds are clamped to the
   * model extent and ignored if they don't describe a positive-area region.
   */
  private resolve_crop_bounds(
    fullX: number,
    fullY: number
  ): { startX: number; endX: number; startY: number; endY: number } | null {
    if (fullX <= 0 || fullY <= 0) return null;

    if (this.crop_params.enabled) {
      const startX = Math.max(0, this.crop_params.x_min ?? 0);
      const endX = Math.min(fullX, this.crop_params.x_max ?? fullX);
      const startY = Math.max(0, this.crop_params.y_min ?? 0);
      const endY = Math.min(fullY, this.crop_params.y_max ?? fullY);
      if (endX <= startX || endY <= startY) return null;
      return { startX, endX, startY, endY };
    }

    // Legacy automatic crop for Shell models, preserved here as presets.
    if (this.image_information.crop_shell) {
      switch (this.image_information.dimension) {
        case "inline":
          return { startX: 11000, endX: 28000, startY: 0, endY: 8500 };
        case "crossline":
          return { startX: 12000, endX: 36000, startY: 0, endY: 8500 };
        case "depth":
          return { startX: 11000, endX: 28000, startY: 12000, endY: 36000 };
        default:
          return null;
      }
    }

    return null;
  }

  /**
   * Validates the manual crop limits and re-plots from the retained original
   * image (no server round-trip, since cropping is purely client-side). The
   * result is also persisted into the saved image_link via contentChange so the
   * crop is restored on reload.
   */
  apply_crop() {
    if (!this.image_object_url) return;

    if (this.crop_params.enabled) {
      const { fullX, fullY } = this.get_full_extent();
      if (fullX <= 0 || fullY <= 0) {
        this.notificationService.error(
          "Crop unavailable",
          "The model extent is not known for this image, so it can't be cropped."
        );
        return;
      }

      const x_min = Math.max(0, Math.min(this.crop_params.x_min ?? 0, fullX));
      const x_max = Math.max(0, Math.min(this.crop_params.x_max ?? fullX, fullX));
      const y_min = Math.max(0, Math.min(this.crop_params.y_min ?? 0, fullY));
      const y_max = Math.max(0, Math.min(this.crop_params.y_max ?? fullY, fullY));

      if (x_max <= x_min || y_max <= y_min) {
        this.notificationService.error(
          "Invalid crop",
          "Each max must be greater than its min and within the model extent."
        );
        return;
      }

      this.crop_params.x_min = x_min;
      this.crop_params.x_max = x_max;
      this.crop_params.y_min = y_min;
      this.crop_params.y_max = y_max;
    }

    this.plot_image();
    this.contentChange.emit(this.build_persistable_link());
  }

  /**
   * Resizes and crops a Base64 image.
   * All co-ordinates and widths are given in real world values, i.e. meters in this case when we are cropping for models.
   * @param base64Image The input Base64 image string
   * @param startX The X-coordinate to start cropping from (top-left of crop area).
   * @param endX The X-coordinate to end cropping at (top-right of crop area).
   * @param trueX True width of the model.
   * @param startY The Y-coordinate to start cropping from (top of crop area).
   * @param endY The Y-coordinate to end cropping at (bottom of crop area).
   * @param trueY True height of the model.
   * @returns A Promise that resolves with the new Base64 image string, or rejects on error.
   */
  resizeAndCropBase64Image(
    base64Image: string,
    startX: number,
    endX: number,
    trueX: number,
    startY: number = null,
    endY: number = null,
    trueY: number = null
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Could not get 2D rendering context for canvas."));
          return;
        }

        let cropWidth = 0;
        let cropHeight = 0;

        cropHeight = img.naturalHeight;

        let modelWidthM = trueX;
        let sx_start = startX * (img.naturalWidth / modelWidthM);
        let sx_end = endX * (img.naturalWidth / modelWidthM);
        cropWidth = sx_end - sx_start;
        canvas.width = this.width;
        canvas.height = this.height;
        this.image_information.dimension;

        let sy_start = 0;
        let sy_end = img.naturalHeight;
        if (trueY !== null && startY !== null && endY !== null) {
          let modelHeightM = trueY;
          sy_start = startY * (img.naturalHeight / modelHeightM);
          sy_end = endY * (img.naturalHeight / modelHeightM);
          cropHeight = sy_end - sy_start;
        }

        // Draw the cropped portion of the original image onto the new canvas
        // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage
        try {
          ctx.drawImage(
            img,
            sx_start, // sx: X to start cropping from original
            sy_start, // sy: Y to start cropping from original
            cropWidth, // sWidth: Width of the cropped area
            cropHeight, // sHeight: Height of the cropped area
            0, // dx: X position on the canvas to draw
            0, // dy: Y position on the canvas to draw
            this.width, // dWidth: Width to draw on canvas (resized width)
            this.height // dHeight: Height to draw on canvas (resized height)
          );
          ctx.imageSmoothingEnabled = false; // Disable image smoothing. needed to remove artifacts

          // Get the new Base64 image from the canvas
          const newBase64 = canvas.toDataURL("image/png");
          resolve(newBase64);
        } catch (error) {
          reject(new Error(`Error drawing image to canvas: ${error}`));
        }
      };

      img.onerror = (error) => {
        reject(new Error(`Failed to load image: ${error}`));
      };

      // Set the src of the image to the Base64 string
      img.src = base64Image;
    });
  }

  onChangeCenteredModelRange(value: number) {
    this.image_plot_parameters.max_val = value;
    this.image_plot_parameters.min_val = -1 * value;
  }

  onChangeModelType(value: string) {
    if (
      this.image_plot_parameters.type == "vdiff" ||
      this.image_plot_parameters.type == "DenChange" ||
      this.image_plot_parameters.type == "RTM" ||
      this.image_plot_parameters.type == "add_perturb vdiff" ||
      this.image_plot_parameters.type == "RWI_Perturb" ||
      this.image_plot_parameters.type == "Total_Vp_Update"
    ) {
      this.centered_model_range = true;
    } else {
      this.centered_model_range = false;
    }

    // VP Ref is only relevant for Total_Vp_Update (the Vp to compare against).
    // Initialise it when selected, clear it otherwise so it isn't sent in the request.
    if (this.image_plot_parameters.type == "Total_Vp_Update") {
      if (this.image_plot_parameters.vp_ref == null) {
        this.image_plot_parameters.vp_ref = 0;
      }
    } else {
      this.image_plot_parameters.vp_ref = null;
    }

    switch (this.image_plot_parameters.type) {
      case "vdiff":
        this.image_plot_parameters.min_val = -200;
        this.image_plot_parameters.max_val = 200;
        this.image_plot_parameters.palette = "greyscale";
        break;
      case "DenChange":
        this.image_plot_parameters.min_val = -0.05;
        this.image_plot_parameters.max_val = 0.05;
        this.image_plot_parameters.palette = "greyscale";
        break;
      case "RWI_Perturb":
        this.image_plot_parameters.min_val = -200;
        this.image_plot_parameters.max_val = 200;
        this.image_plot_parameters.palette = "greyscale";
        break;
      case "Total_Vp_Update":
        this.image_plot_parameters.min_val = -200;
        this.image_plot_parameters.max_val = 200;
        this.image_plot_parameters.palette = "red_white_blue";
        break;
      case "RTM":
        this.image_plot_parameters.min_val = -0.005;
        this.image_plot_parameters.max_val = 0.005;
        this.image_plot_parameters.palette = "greyscale";
        break;
      case "add_perturb vdiff":
        this.image_plot_parameters.min_val = -600;
        this.image_plot_parameters.max_val = 600;
        this.image_plot_parameters.palette = "greyscale";
        break;
      case "Vp":
        this.image_plot_parameters.min_val = 1500;
        this.image_plot_parameters.max_val = 5000;
        this.image_plot_parameters.palette = "SEGRefl2";
        break;
      default:
        break;
    }
  }

  get_iteration_shots() {
    if (!this.image_information.job_id) {
      this.notificationService.error(
        "Error",
        "There was an error getting the shots for the specified job. The Job ID is not available."
      );
      return;
    }
    // y: costItem ? (this.is2D ? costItem.sz : costItem.sy) : null,
    // x: costItem ? costItem.sx : null,
    let is_2d: boolean = this.image_information.model_grid.is_2d;
    let iteration: number = this.image_information.iteration;
    let job_id: string = this.image_information.job_id;
    let total_iterations: number = this.image_information.total_iterations;
    let x2_is_inline: boolean = this.image_information.x2_is_inline;
    forkJoin({
      all_shots: this.cachedProjectService.getShotOverlay(job_id, iteration),
      shots_positions: this.cachedProjectService
        .getCosts(job_id, total_iterations, x2_is_inline)
        .pipe(
          map((res) => {
            // slight transformation to the data, cause this is all we need.
            // making array to dict makes seeking easier later.
            let shots_dict: { [key: string]: { x: number; y: number } } = {};
            if (is_2d) {
              res.costItems.forEach((datum) => {
                shots_dict[datum.shotNo] = { x: datum.sx, y: datum.sz };
              });
            } else {
              res.costItems.forEach((datum) => {
                shots_dict[datum.shotNo] = { x: datum.sx, y: datum.sy };
              });
            }
            return shots_dict;
          })
        ),
      dumped_shot_ids: this.cachedProjectService.getTraceFilesByIteration(
        job_id,
        iteration
      ),
    })
      .toPromise()
      .then((res) => {
        this.plot_shots_on_depth_chart(
          res.all_shots,
          res.shots_positions,
          res.dumped_shot_ids
        );
      })
      .catch((err) => {
        console.log("error", err);
        this.notificationService.error(
          "Error",
          err?.error?.detail || "An unexpected error occurred"
        );
      });
  }

  plot_shots_on_depth_chart(
    all_shots: ShotOverlayDto[],
    shots_positions: { [key: string]: { x: number; y: number } },
    dumped_shot_ids: number[]
  ) {
    this.svg
      .selectAll(".shot-point") // Select all elements with class 'shot-point' (initially none)
      .data(all_shots) // Bind our shots array
      .enter() // For each new data point, create an element
      .append("circle") // Append a circle
      .attr("class", "shot-point")
      .attr("cx", (d) => {
        let x_pos = shots_positions[d["shot_id"]].x;
        return this.xScale(x_pos);
      }) // Use xScale to convert data.x to pixel.x
      .attr("cy", (d) => {
        let y_pos = shots_positions[d["shot_id"]].y;
        return this.yScale(y_pos);
      }) // Use yScale to convert data.y to pixel.y
      .attr("r", 3) // Radius of the circle
      .attr("stroke", (d) =>
        dumped_shot_ids.includes(d["shot_id"]) ? "red" : ""
      )
      .on("mouseover", (event, d) => {
        let targetEl = d3.select(event.target);
        targetEl
          .transition()
          .duration(100)
          .attr("r", this.shot_radius * 1.5);

        let leftPos =
          this.width - parseFloat(targetEl.attr("cx")) >= 200
            ? Number(targetEl.attr("cx")) + 10
            : Number(targetEl.attr("cx")) - 120;
        this.shot_tooltip
          .style("left", `${leftPos}px`)
          .style("top", `${Number(targetEl.attr("cy")) - 20}px`)
          .html(
            `
            <strong>ID:</strong> <span>${d["shot_id"]}</span><br/>
            <strong>Trace Fit:</strong> <span>${d["traces_fit"]}</span>`
          )
          .transition()
          .duration(100)
          .style("opacity", 1);
      }) // Add mouseover event listener
      .on("mouseout", (event, d) => {
        d3.select(event.target)
          .transition()
          .duration(100)
          .attr("r", this.shot_radius);
        this.shot_tooltip.transition().duration(100).style("opacity", 0); // Make it invisible
      });

    this.update_shot_colors();
    this.apply_shot_trace_fit_filter();
  }

  /**
   * Returns true when a shot has a usable traces_fit value (not null/undefined/NaN).
   */
  private has_trace_fit(d: ShotOverlayDto): boolean {
    return d?.traces_fit != null && !isNaN(d.traces_fit);
  }

  /**
   * Hides or shows shots that don't have a traces_fit value, based on the
   * hide_shots_without_trace_fit toggle. Toggles the SVG circles' display
   * rather than re-fetching, so it stays in sync with the currently plotted shots.
   */
  apply_shot_trace_fit_filter() {
    this.svg
      .selectAll("circle.shot-point")
      .style("display", (d: ShotOverlayDto) =>
        this.hide_shots_without_trace_fit && !this.has_trace_fit(d)
          ? "none"
          : null
      );
  }

  update_shot_colors() {
    const colorScale = d3
      .scaleLinear()
      .domain([this.trace_color_min, this.trace_color_max])
      .range(["black", "white"] as any);
    this.svg
      .selectAll("circle.shot-point")
      .attr("fill", (d) => colorScale(d["traces_fit"]));
  }

  remove_shots_on_depth_chart() {
    this.svg.selectAll("circle.shot-point").remove();
  }

  onChangeShowShots() {
    this.loading_shot = true;
    if (this.show_shots) {
      this.get_iteration_shots();
    } else {
      this.remove_shots_on_depth_chart();
    }
    this.loading_shot = false;
  }

  onChangeShotRadius() {
    this.svg.selectAll("circle.shot-point").attr("r", this.shot_radius); // Radius of the circle
  }

  @HostListener("window:resize", ["$event"])
  sizeChange(event) {
    this.update_image_width();
  }
}
